import type { SxProps, Theme } from '@mui/material/styles';
import { MAX_CONTENT_WIDTH, gridColumns, gridGap, pagePadding, sectionSpacing } from './tokens';

/**
 * The page's horizontal rhythm, in one place because the AppBar, the lifecycle
 * rail and every page's content have to agree.
 *
 * **Padding is on the outer element and the width cap is on the inner one.**
 * Both on one element subtracts the gutter from the cap, so 1320 with a 60px
 * gutter renders 1200 of content.
 */

/** The gutter. Goes on the full-bleed outer element. */
export const pageGutterSx: SxProps<Theme> = {
  paddingInline: {
    xs: `${String(pagePadding.mobile)}px`,
    sm: `${String(pagePadding.tablet)}px`,
    md: `${String(pagePadding.desktop)}px`,
  },
};

/** The width cap. Goes on the element inside the gutter. */
export const contentWidthSx: SxProps<Theme> = {
  maxWidth: MAX_CONTENT_WIDTH,
  marginInline: 'auto',
  width: '100%',
};

/**
 * Gap between cards in a grid. Not a spacing-scale multiple — the design's
 * 20 / 16 / 12 is its own progression.
 */
export const gridGapSx = {
  gap: {
    xs: `${String(gridGap.mobile)}px`,
    sm: `${String(gridGap.tablet)}px`,
    md: `${String(gridGap.desktop)}px`,
  },
} satisfies SxProps<Theme>;

/**
 * Air between the major blocks of a page. As `marginTop`, so the first block on
 * a page does not get one — a page's own top padding owns that.
 */
export const sectionSpacingSx = {
  marginTop: {
    xs: `${String(sectionSpacing.mobile)}px`,
    sm: `${String(sectionSpacing.tablet)}px`,
    md: `${String(sectionSpacing.desktop)}px`,
  },
} satisfies SxProps<Theme>;

/**
 * A grid of cards. `stage` is the L1 lifecycle grid, whose columns are stages;
 * `card` is any grid of peers.
 *
 * **`minmax(0, 1fr)` rather than `1fr`.** A track's default minimum is its
 * content, so one long unbroken Confluence title widens its column and pushes
 * the row past the viewport.
 */
export const cardGridSx = (kind: keyof typeof gridColumns) =>
  ({
    display: 'grid',
    ...gridGapSx,
    gridTemplateColumns: {
      xs: `repeat(${String(gridColumns[kind].mobile)}, minmax(0, 1fr))`,
      sm: `repeat(${String(gridColumns[kind].tablet)}, minmax(0, 1fr))`,
      md: `repeat(${String(gridColumns[kind].desktop)}, minmax(0, 1fr))`,
    },
  }) satisfies SxProps<Theme>;

/**
 * The search page's two columns: results, with the filter panel beside them.
 * 1000 / 320 as fractions rather than pixels, so the pair fills whatever width
 * is left after the gutters.
 *
 * **Named areas rather than source order**, because the two orders disagree.
 * The filters come first in the DOM — below desktop the panel belongs *above*
 * the results, since a control under fifty results is one nobody finds — and
 * still render on the right where there is room for a column.
 */
export const searchColumnsSx = {
  display: 'grid',
  gap: 8,
  alignItems: 'start',
  gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1000fr) minmax(0, 320fr)' },
  gridTemplateAreas: {
    xs: '"filters" "results"',
    md: '"results filters"',
  },
} satisfies SxProps<Theme>;
